import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { useUnit } from '../../context/UnitContext';
import { 
  getUnitSymbol, 
  calculateHousingBillByLiters 
} from '../../utils/unitConversion';
import useChartTokens from '../../theme/chartTokens';
import './MonthlyComparisonChart.css';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PredictionsIcon from '@mui/icons-material/Insights';

export default function MonthlyComparisonChart({ 
  currentMonth, 
  previousMonth,
  twoMonthsAgo = 0,
  savingsPercentage, 
  savingsAmount, 
  title = "Comparación de consumo mensual",
  daysElapsed = null,
  showBillPrediction = true,
  useProjectedTrend = false
}) {
  const { selectedUnit, convertValue } = useUnit();
  const chart = useChartTokens();
  
  // Nombres de los meses
  const getMonthName = (offset = 0) => {
    const date = new Date();
    date.setMonth(date.getMonth() - offset);
    return date.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
  };

  /* Los dos meses cerrados van en tono neutro y el mes en curso en el color
     del agua: así se lee de un vistazo cuál es 'ahora' y cuáles son 'antes'.
     Antes eran tres azules distintos y ninguno significaba nada. */
  const data = [
    {
      name: getMonthName(2),
      consumo: convertValue(twoMonthsAgo),
      original: twoMonthsAgo,
      fill: chart.compare
    },
    {
      name: getMonthName(1),
      consumo: convertValue(previousMonth),
      original: previousMonth,
      fill: chart.compare
    },
    {
      name: getMonthName(0),
      consumo: convertValue(currentMonth),
      original: currentMonth,
      fill: chart.water
    }
  ];

  // Calcular predicción mejorada basada en tendencia de 3 meses
  const calculateImprovedPrediction = () => {
    if (!daysElapsed || daysElapsed === 0) return null;
    
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const dailyAverage = currentMonth / daysElapsed;
    const predictedMonthly = dailyAverage * daysInMonth;
    
    // Calcular tendencia basada en los 3 meses
    // El mes actual aún está incompleto: para la tendencia se compara su
    // proyección con meses cerrados, aunque la barra muestre lo medido hasta hoy.
    const closedMonths = [twoMonthsAgo, previousMonth].filter(m => m > 0);
    const monthsWithData = (useProjectedTrend
      ? [...closedMonths, predictedMonthly]
      : [twoMonthsAgo, previousMonth, currentMonth]).filter(m => m > 0);
    let trend = 'stable';
    let trendPercentage = 0;
    
    if (monthsWithData.length >= 2) {
      const firstMonth = monthsWithData[0];
      const lastMonth = monthsWithData[monthsWithData.length - 1];
      trendPercentage = ((lastMonth - firstMonth) / firstMonth) * 100;
      
      if (trendPercentage > 10) trend = 'increasing';
      else if (trendPercentage < -10) trend = 'decreasing';
    }
    
    // Calcular confianza basada en días transcurridos
    let confidence = 'low';
    if (daysElapsed >= 20) confidence = 'high';
    else if (daysElapsed >= 10) confidence = 'medium';
    
    // Predicción de factura
    const predictedBill = calculateHousingBillByLiters(predictedMonthly, 'governmental');
    
    // Promedio histórico para referencia
    const referenceMonths = useProjectedTrend ? closedMonths : monthsWithData;
    const historicalAverage = referenceMonths.length > 0
      ? referenceMonths.reduce((a, b) => a + b, 0) / referenceMonths.length
      : 0;
    
    return {
      predictedConsumption: predictedMonthly,
      predictedBill: predictedBill.total,
      dailyAverage,
      daysAnalyzed: daysElapsed,
      daysRemaining: daysInMonth - daysElapsed,
      confidence,
      trend,
      trendPercentage: Math.abs(trendPercentage).toFixed(1),
      historicalAverage,
      message: confidence === 'high' 
        ? 'Predicción confiable basada en datos suficientes'
        : confidence === 'medium'
        ? 'Predicción moderada, aún recopilando datos'
        : 'Predicción preliminar, faltan más datos'
    };
  };

  const prediction = showBillPrediction ? calculateImprovedPrediction() : null;

  // Comparación con mes anterior
  const previousMonthBill = previousMonth > 0 
    ? calculateHousingBillByLiters(previousMonth, 'governmental')
    : null;

  const billComparison = prediction && previousMonthBill ? {
    difference: prediction.predictedBill - previousMonthBill.total,
    trend: prediction.predictedBill > previousMonthBill.total * 1.05 
      ? 'increase' 
      : prediction.predictedBill < previousMonthBill.total * 0.95 
        ? 'decrease' 
        : 'stable',
    message: prediction.predictedBill > previousMonthBill.total
      ? `+$${(prediction.predictedBill - previousMonthBill.total).toFixed(2)} vs mes anterior`
      : prediction.predictedBill < previousMonthBill.total
        ? `-$${(previousMonthBill.total - prediction.predictedBill).toFixed(2)} vs mes anterior`
        : 'Similar al mes anterior'
  } : null;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const bill = calculateHousingBillByLiters(data.original, 'governmental');
      return (
        <div className="ng-chart-tooltip">
          <p className="ng-chart-tooltip__label">{data.name}</p>
          <p className="ng-chart-tooltip__value">
            <span className="ng-chart-tooltip__dot" style={{ background: data.fill }} />
            Consumo <strong>{`${data.consumo.toFixed(2)} ${getUnitSymbol(selectedUnit)}`}</strong>
          </p>
          <p className="ng-chart-tooltip__value">
            <span className="ng-chart-tooltip__dot" style={{ background: chart.success }} />
            Costo <strong>${bill.total.toFixed(2)} MXN</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increase':
      case 'increasing':
        return <TrendingUpIcon className="trend-icon trend-up" />;
      case 'decrease':
      case 'decreasing':
        return <TrendingDownIcon className="trend-icon trend-down" />;
      default:
        return <TrendingFlatIcon className="trend-icon trend-stable" />;
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'var(--success-color)';
      case 'medium': return 'var(--warning-color)';
      default: return 'var(--error-color)';
    }
  };

  // Calcular el promedio para la línea de referencia
  const monthsWithConsumption = data.filter((item) => item.consumo > 0);
  const average = useProjectedTrend
    ? (monthsWithConsumption.length
      ? monthsWithConsumption.reduce((sum, item) => sum + item.consumo, 0) / monthsWithConsumption.length
      : 0)
    : data.reduce((sum, item) => sum + item.consumo, 0) / data.filter(d => d.consumo > 0).length;

  return (
    <div className="monthly-comparison-container">
      <h4 className="monthly-comparison-title">{title}</h4>
      
      {/* Resumen de tendencia */}
      {prediction && prediction.trend !== 'stable' && (
        <div className={`trend-summary ${prediction.trend}`}>
          {getTrendIcon(prediction.trend)}
          <span>
            Tendencia {prediction.trend === 'increasing' ? 'al alza' : 'a la baja'}: 
            {' '}{prediction.trendPercentage}% en 3 meses
          </span>
        </div>
      )}

      {/* Gráfico de barras mejorado con 3 meses */}
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 24, left: 0, bottom: 5 }}
          barCategoryGap="32%"
        >
          <CartesianGrid stroke={chart.grid} vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fontWeight: 500, fill: chart.axis }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: chart.axis, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${value.toFixed(0)}`}
            width={36}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: chart.grid, fillOpacity: 0.5 }} />
          {average > 0 && (
            <ReferenceLine
              y={average}
              stroke={chart.goal}
              strokeDasharray="5 5"
              label={{ value: 'Promedio', fill: chart.goal, fontSize: 11 }}
            />
          )}
          <Bar
            dataKey="consumo"
            radius={[3, 3, 0, 0]}
            maxBarSize={64}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Leyenda de meses */}
      <div className="months-legend">
        {data.map((item, index) => (
          <div key={index} className="legend-item">
            <span className="legend-color" style={{ backgroundColor: item.fill }}></span>
            <span className="legend-text">
              {item.name}: {item.consumo.toFixed(1)} {getUnitSymbol(selectedUnit)}
            </span>
          </div>
        ))}
      </div>

      {/* Predicción inteligente mejorada */}
      {prediction && (
        <div className="bill-prediction-section">
          <div className="prediction-header">
            <PredictionsIcon className="prediction-icon" />
            <h5 className="prediction-title">Predicción del Mes Actual</h5>
          </div>
          
          <div className="prediction-content">
            <div className="prediction-main">
              <div className="predicted-bill">
                <span className="bill-amount">${prediction.predictedBill.toFixed(2)}</span>
                <span className="bill-label">Factura estimada</span>
              </div>
              
              <div className="predicted-consumption">
                <span className="consumption-amount">
                  {convertValue(prediction.predictedConsumption).toFixed(1)} {getUnitSymbol(selectedUnit)}
                </span>
                <span className="consumption-label">Consumo proyectado</span>
              </div>
            </div>
            
            {billComparison && (
              <div className={`bill-comparison ${billComparison.trend}`}>
                {getTrendIcon(billComparison.trend)}
                <span className="comparison-text">
                  {billComparison.message}
                </span>
              </div>
            )}
            
            <div className="prediction-details">
              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Promedio diario:</span>
                  <span className="detail-value">
                    {convertValue(prediction.dailyAverage).toFixed(2)} {getUnitSymbol(selectedUnit)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Días analizados:</span>
                  <span className="detail-value">{prediction.daysAnalyzed} días</span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Días restantes:</span>
                  <span className="detail-value">{prediction.daysRemaining} días</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Promedio histórico:</span>
                  <span className="detail-value">
                    {convertValue(prediction.historicalAverage).toFixed(1)} {getUnitSymbol(selectedUnit)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="confidence-indicator">
              <span 
                className={`confidence-badge ${prediction.confidence}`}
                style={{ color: getConfidenceColor(prediction.confidence) }}
              >
                Confianza {prediction.confidence === 'high' ? 'Alta' : 
                          prediction.confidence === 'medium' ? 'Media' : 'Baja'}
              </span>
              <span className="confidence-message">{prediction.message}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
